/**
 * This is a very thin wrapper around DOM Storage that mainly adds the functionality to save objects and arrays.
 * The use of try / catch in this module is to suppress exceptions that could be caused by any of the following:
 *
 *  1. The browser may not support localStorage (TypeError)
 *  2. Private browsing on iOS throws an exception if you try to use localStorage (QUOTA_EXCEEDED_ERR)
 *  3. Storage quota may have been reached (QUOTA_EXCEEDED_ERR)
 *  4. JSON.parse() throws exceptions for invalid JSON strings (SyntaxError)
 *
 *  As all operations will fail silently the functionality provided by this module should always be
 *  treated as a 'nice-to-have' and not a requirement.
 */
import _ from "underscore";

/**
 * Shorthand for localStorage.
 * @type {Storage}
 */
var local;
try {
    local = window.localStorage;
} catch (e) {
    console.error("Unable to access localStorage.");
}

/**
 * Shorthand for sessionStorage.
 * @type {Storage}
 */
var session;
try {
    session = window.sessionStorage;
} catch (e) {
    console.error("Unable to access sessionStorage.");
}

export default {
    /**
     * Returns a previously saved value
     * @param {String} key Key of the item to retrieve.
     * @param {*} [defaultValue] Default value to return if key doesn't exist.
     *      If not provided null will be returned.
     * @param {boolean=false} [fromSession] get the value from session storage
     * @returns {*} The value stored under the given key or null.
     */
    get: function(key, defaultValue, fromSession) {
        if (arguments.length === 1) {
            defaultValue = null;
        }

        try {
            var store = fromSession ? session : local;
            var value = store.getItem(key);
            value = value === null ? defaultValue : value;

            try {
                return JSON.parse(value);
            } catch (e) {
                return value;
            }
        } catch (e) {
            console.error(e);
            return defaultValue;
        }
    },

    /**
     * Sets a new item.
     * @param {String} key Key to save the item under.
     * @param {String|Number|Object|Array} value Value to save, if it is an object or array it will be stringified.
     * @param {boolean=false} [toSession] set the value to session storage
     */
    set: function(key, value, toSession) {
        // this is not wrapped in try catch as provided an object or array which cannot be JSON serialized is an error.
        if (_.isObject(value)) {
            value = JSON.stringify(value);
        }

        try {
            var store = toSession ? session : local;
            store.setItem(key, value);
        } catch (e) {
            console.error(e);
        }
    },

    /**
     * Removes a previously saved item.
     * @param {String} key
     * @param {boolean=false} [fromSession] remove from the session store
     */
    remove: function(key, fromSession) {
        try {
            if (fromSession) {
                session.removeItem(key);
            } else {
                local.removeItem(key);
            }
        } catch (e) {
            console.error(e);
        }
    },

    /**
     * Increments the value of `key` by 1. If key doesn't exist it will be set to 1.
     * If key exists and the value is not a number it will be set to 1.
     * @param key
     */
    increment: function(key) {
        var curr = this.get(key, 0);
        curr = _.isNumber(curr) ? curr + 1 : 1;
        this.set(key, curr);
        return curr;
    },

    /**
     * @returns {boolean} true if localStorage is supported, otherwise false.
     */
    isSupported: _.once(function() {
        var key = "test-key" + Math.random() + Math.random();
        var result;

        this.set(key, "testing");
        result = this.get(key) === "testing";
        this.remove(key);

        return result;
    }),
};
